import api from './api';
import type { StatusTone } from '../components/ui';

/** Lage zum heutigen Tag, vom Server berechnet (vertrag.service fristen()). */
export type FristLage = 'offen' | 'bald' | 'endet' | 'unbefristet' | 'gekuendigt' | 'abgelaufen' | 'inaktiv';

export interface Fristen {
  lage: FristLage;
  laufzeitEnde: string | null;
  kuendigungBis: string | null;
  fruehestesEnde: string | null;
  tageBisTermin: number | null;
}

export interface VertragKurz {
  id: string;
  number?: string | null;
  titel: string;
  art: string;
  status?: string;
}

export interface Vertrag {
  id: string;
  number?: string | null;
  titel: string;
  art: string;
  status: string;
  companyId?: string | null;
  dealId?: string | null;
  quoteId?: string | null;
  hauptvertragId?: string | null;
  unterzeichnetAm?: string | null;
  beginn?: string | null;
  ende?: string | null;
  verlaengerungMonate: number;
  kuendigungsfristWert?: number | null;
  kuendigungsfristEinheit: string;
  gekuendigtAm?: string | null;
  erinnerungVorlaufTage: number;
  wertCents?: number | null;
  wertIntervall?: string | null;
  currency: string;
  paperlessDocumentId?: string | null;
  ablagePfad?: string | null;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
  company?: { id: string; name: string } | null;
  deal?: { id: string; title: string } | null;
  quote?: { id: string; number: string; title?: string | null } | null;
  hauptvertrag?: VertragKurz | null;
  anlagen?: VertragKurz[];
  _count?: { anlagen: number };
  fristen: Fristen;
}

export type VertragDaten = Partial<Omit<Vertrag, 'id' | 'number' | 'company' | 'deal' | 'quote' | 'hauptvertrag' | 'anlagen' | '_count' | 'fristen' | 'createdAt' | 'updatedAt'>>;

export const VERTRAG_ART_LABEL: Record<string, string> = {
  dienstleistung: 'Dienstleistungsvertrag',
  werkvertrag: 'Werkvertrag',
  rahmenvertrag: 'Rahmenvertrag',
  einzelvertrag: 'Einzelvertrag',
  avv: 'Auftragsverarbeitung (AVV)',
  nda: 'Geheimhaltung (NDA)',
  partner: 'Partnervereinbarung',
  wartung: 'Wartung/Support',
  lizenz: 'Lizenz',
  sonstiges: 'Sonstiges',
};

export const VERTRAG_STATUS_LABEL: Record<string, string> = {
  entwurf: 'Entwurf',
  verhandlung: 'In Verhandlung',
  aktiv: 'Aktiv',
  gekuendigt: 'Gekündigt',
  beendet: 'Beendet',
};

export const VERTRAG_STATUS_TONE: Record<string, StatusTone> = {
  entwurf: 'info',
  verhandlung: 'warning',
  aktiv: 'success',
  gekuendigt: 'warning',
  beendet: 'neutral',
};

export const FRIST_EINHEIT_LABEL: Record<string, string> = { tage: 'Tage', wochen: 'Wochen', monate: 'Monate' };
export const WERT_INTERVALL_LABEL: Record<string, string> = { einmalig: 'einmalig', monatlich: 'pro Monat', jaehrlich: 'pro Jahr' };

export const FRIST_LAGE_TONE: Record<FristLage, StatusTone> = {
  offen: 'success',
  bald: 'danger',
  endet: 'info',
  unbefristet: 'neutral',
  gekuendigt: 'warning',
  abgelaufen: 'neutral',
  inaktiv: 'neutral',
};

const tag = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('de-DE', { timeZone: 'UTC' }) : '');

/** Ein Satz zur Frist, z. B. "kündigen bis 30.09.2026 (in 20 Tagen)". */
export function fristText(f: Fristen): string {
  const inTagen = f.tageBisTermin === null ? '' : f.tageBisTermin === 0 ? ' (heute)' : f.tageBisTermin > 0 ? ` (in ${f.tageBisTermin} Tagen)` : '';
  switch (f.lage) {
    case 'offen':
    case 'bald':
      return f.kuendigungBis ? `kündigen bis ${tag(f.kuendigungBis)}${inTagen}, sonst Verlängerung` : `läuft bis ${tag(f.laufzeitEnde)}`;
    case 'endet':
      return `endet am ${tag(f.laufzeitEnde)}${inTagen}`;
    case 'unbefristet':
      return f.fruehestesEnde ? `unbefristet, frühestens ${tag(f.fruehestesEnde)} kündbar` : 'unbefristet';
    case 'gekuendigt':
      return `gekündigt zum ${tag(f.laufzeitEnde)}`;
    case 'abgelaufen':
      return `abgelaufen am ${tag(f.laufzeitEnde)}`;
    default:
      return '';
  }
}

export interface ImportZeile {
  firma?: string | null;
  firmaKurz?: string | null;
  titel: string;
  art?: string;
  status?: string;
  hauptvertragTitel?: string | null;
  unterzeichnetAm?: string | null;
  beginn?: string | null;
  ende?: string | null;
  verlaengerungMonate?: number | null;
  kuendigungsfristWert?: number | null;
  kuendigungsfristEinheit?: string | null;
  wertCents?: number | null;
  wertIntervall?: string | null;
  ablagePfad?: string | null;
  notes?: string | null;
}

export interface ImportErgebnis {
  zeile: number;
  titel: string;
  aktion: 'angelegt' | 'wuerde-anlegen' | 'vorhanden' | 'fehler';
  firma: { id: string | null; name: string; neu: boolean } | null;
  hauptvertrag: string | null;
  vertragId?: string;
  number?: string | null;
  fehler?: string;
}

export interface ImportAntwort {
  probelauf: boolean;
  ergebnisse: ImportErgebnis[];
  zusammenfassung: Record<string, number>;
}

const STATUS_WERTE = Object.keys(VERTRAG_STATUS_LABEL);

/**
 * Importdatei -> Zeilen. Nimmt eine Liste von Zeilen oder ein Objekt mit
 * `vertraege` (Ausgabe der Ablage-Extraktion: `statusVorschlag`,
 * `hauptquelle`, `unsicher`, `belege`). Unsichere Felder und die Quelle
 * landen in den Notizen, damit sie in der Akte nachpruefbar bleiben.
 */
export function importZeilenAus(json: unknown): ImportZeile[] {
  const liste: unknown = Array.isArray(json) ? json : (json as any)?.vertraege;
  if (!Array.isArray(liste)) throw new Error('Erwartet wird eine Liste oder ein Objekt mit "vertraege".');
  return liste.map((roh: any) => {
    const status = roh.status ?? roh.statusVorschlag;
    const notizen = [
      roh.notes,
      Array.isArray(roh.unsicher) && roh.unsicher.length ? `Import: unsicher – ${roh.unsicher.join(', ')}` : null,
      roh.statusBegruendung ? `Status: ${roh.statusBegruendung}` : null,
    ].filter(Boolean).join('\n');
    const ablage = roh.ablagePfad ?? (roh.hauptquelle ? `SharePoint Vertrieb & Buchhaltung: ${roh.hauptquelle}` : null);
    return {
      firma: roh.firma ?? null,
      firmaKurz: roh.firmaKurz ?? null,
      titel: String(roh.titel ?? '').trim(),
      art: roh.art || undefined,
      status: STATUS_WERTE.includes(status) ? status : 'entwurf',
      hauptvertragTitel: roh.hauptvertragTitel ?? null,
      unterzeichnetAm: roh.unterzeichnetAm ?? null,
      beginn: roh.beginn ?? null,
      ende: roh.ende ?? null,
      verlaengerungMonate: roh.verlaengerungMonate ?? 0,
      kuendigungsfristWert: roh.kuendigungsfristWert ?? null,
      kuendigungsfristEinheit: roh.kuendigungsfristEinheit || 'monate',
      wertCents: roh.wertCents ?? null,
      wertIntervall: roh.wertIntervall ?? null,
      ablagePfad: ablage,
      notes: notizen || null,
    };
  });
}

const vertragService = {
  async list(filter?: { companyId?: string; status?: string; art?: string; q?: string; faelligInTagen?: number }): Promise<Vertrag[]> {
    const response = await api.get('/vertraege', { params: filter || {} });
    return response.data;
  },
  async get(id: string): Promise<Vertrag> {
    const response = await api.get(`/vertraege/${id}`);
    return response.data;
  },
  async create(daten: VertragDaten & { titel: string }): Promise<Vertrag> {
    const response = await api.post('/vertraege', daten);
    return response.data;
  },
  async update(id: string, daten: VertragDaten): Promise<Vertrag> {
    const response = await api.patch(`/vertraege/${id}`, daten);
    return response.data;
  },
  async importieren(zeilen: ImportZeile[], optionen: { firmenAnlegen: boolean; probelauf: boolean }): Promise<ImportAntwort> {
    const response = await api.post('/vertraege/import', { zeilen, ...optionen });
    return response.data;
  },
};

export default vertragService;
